import React from 'react';
import {
  Colors,
  Divider,
  Flex,
  IconArrowRight,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import dayjs from 'dayjs';
import { isNotNil } from 'shared/src/utils';
import type { GoodsContactsOrderItem } from 'shared/src/api';
import { useGoodsContacts } from '../../../../../queries/useGoodsContacts';

const THUMBNAIL_SIZE = 54;

// 웹뷰 원본의 gap(19/5/3px)은 ui-kit spacing 스케일(2·4·8·12·16·20·24)에 없어 가장 가까운
// 값으로 맞췄다: 19→20, 5→4, 3→4. 1px 차이라 시각적 차이는 없다.
const ITEM_COLUMN_GAP = 20;
const ROW_COLUMN_GAP = 4;
const TEXT_ROW_GAP = 4;

/**
 * 문의 탭 상단의 "주문한 상품이 궁금하신가요?" 블록.
 *
 * ably-webview-react `pages/goods/_component/Contact/InquiryOrderedGoodsInformation.tsx` 이식.
 * 주문 내역이 없으면(비로그인 포함) 아무것도 그리지 않는다 — 웹뷰와 동일.
 */
export const InquiryOrderedGoodsInformation: React.FC<{
  onPressOrderItem: (orderItem: GoodsContactsOrderItem) => void;
}> = ({ onPressOrderItem }) => {
  const { data } = useGoodsContacts();

  if (!data || data.order_items.length === 0) return null;

  return (
    <>
      <Flex padding={{ top: 30, bottom: 16, horizontal: 16 }} rowGap={12}>
        <Typography type="h5" color="content_primary">
          주문한 상품이 궁금하신가요?
        </Typography>
        <Typography type="body2" color="content_secondary">
          주문취소 / 교환 / 반품은 주문 상세에서 접수해주세요
        </Typography>
      </Flex>
      {data.order_items.map(orderItem => (
        <Flex.Row
          key={orderItem.sno}
          padding={{ top: 12, bottom: 8, horizontal: 16 }}
          columnGap={ITEM_COLUMN_GAP}
          onPress={() => onPressOrderItem(orderItem)}
        >
          <RemoteImage
            uri={orderItem.goods.image_still}
            width={THUMBNAIL_SIZE}
            height={THUMBNAIL_SIZE}
            resizeMode="cover"
            borderRadius="small"
          />
          <Flex.Row flex={1} alignItems="center" columnGap={ROW_COLUMN_GAP}>
            <Flex flex={1} rowGap={TEXT_ROW_GAP}>
              {isNotNil(orderItem.delivery_information_message) && (
                <Typography type="subtitle2" color="content_primary">
                  {orderItem.delivery_information_message}
                </Typography>
              )}
              <Typography type="body3" color="content_tertiary">
                주문일 {dayjs(orderItem.ordered_at).format('YYYY.MM.DD')}
              </Typography>
            </Flex>
            <IconArrowRight
              width={16}
              height={16}
              fill={Colors.content_tertiary}
            />
          </Flex.Row>
        </Flex.Row>
      ))}
      <Divider size="medium" />
    </>
  );
};
